// PDF 出力範囲の外へ初めて図形を置いた利用者へ、新しいキャンバスへの移行を検討するよう、
// キャンバス内のお知らせ（NoticeHost）で警告を一度だけ出す。キャンバスごと・利用者ごとに別管理する。
//
// 1. dispatcher から「利用者自身の配置操作として送った op」（作成・貼り付け・複製・ペンの確定・移動・
//    リサイズ・回転、画像・動画はアップロード成功時の公開 URL 反映）を受け取り、clientOpId で確定待ちとして控える。
// 2. realtime の op.commit（サーバの確定）を受け取ったら、その op が doc store の確定状態
//    （committedObjects / committedMeta）へ serverSeq の欠番なく畳み込まれるのを待ち、その時点の確定状態で
//    図形が PDF 出力範囲（確定状態のサイズプリセットのキャンバス矩形）へ完全に収まらないかを、PDF の描画規則に
//    合わせた外接矩形（canvas-object-visual-bounds）で判定する。まだ確定していない楽観適用（inFlightOps）は
//    含めない（後で拒否される移動やサイズ変更で表示権を消費しないため）。確定が順不同で届いても、先行する
//    確定が畳み込まれてから判定する。欠番が埋まって複数の確定がまとめて畳み込まれたときに、後続の確定が同じ
//    図形（コネクタの端点を含む）の外接矩形や出力範囲を変えていれば（スナップショットの取り直しなどで後続を
//    確かめられない場合も）、確定状態はその配置の結果を表さないため判定しない（他者の操作の結果を利用者の
//    配置として数えない）。
//    op.reject（失敗・ロールバック）は表示権を消費しない。競合で再送された移動は、再送後の op を控え直す。
// 3. 範囲外なら、サーバへ表示権を要求（claim）し、勝者（show: true）のときだけ警告を積む。
//    同じ利用者の別タブ・別端末からの同時要求は、サーバの条件付き書き込みで一方だけが表示する。
//    送信に失敗しても配置は取り消さず、同じ attemptId で再試行する。それでも失敗したら未通知として残し、
//    次に範囲外への配置が確定したときに同じ attemptId から要求し直す（応答だけが失われた場合も、
//    同じ attemptId なら勝者の結果を受け取り直せる）。
//
// タブ内の記録（応答済み・未確定の attemptId）は利用者 × キャンバスごとに分ける。ページを再読み込みせずに
// 利用者が切り替わっても、前の利用者の記録で次の利用者の判定を止めない。
//
// キャンバスを開いただけ・他参加者の操作の受信・pan / zoom・サイズプリセットの変更・Undo / Redo の再生・
// ドラッグ中のプレビューは、そもそも配置操作として渡されないため通知しない。
//
// 表示の厳密な視認までは保証しない（表示権の確定後にタブを閉じた場合など）。二重表示を避ける
// 「最大 1 回」を優先する。

import { useCallback, useEffect, useRef } from "react";
import { customAlphabet } from "nanoid";
import {
  CANVAS_OBJECT_HARD_LIMIT,
  isDrawableObject,
  normalizeCanvasSizePreset,
  resolveCanvasRect,
  type CanvasFrame,
  type CanvasObject,
  type CanvasOp,
  type ServerMessage,
  type TransformObjectOp,
} from "@refinement-canvas/domain";
import { CanvasApiHttpError, useCanvasApi } from "../../lib/canvas-api";
import {
  isObjectOutsidePdfFrame,
  type CanvasObjectLookup,
} from "../../lib/canvas-object-visual-bounds";
import { useRealtimeClient } from "../../realtime/realtime-context";
import { isPendingAttachmentSrc } from "./attachment-placeholder";
import { useShowToast } from "./notice/use-show-toast";
import { useCanvasDocStore } from "./store/canvas-doc-store";

/** 警告の固定文言。 */
export const PDF_OUTSIDE_NOTICE_MESSAGE =
  "PDF出力可能範囲を越えています。新しいキャンバスへの移行を検討してください。";

/** 送信失敗時の再試行の間隔（ms）。最初の送信に続けて、同じ attemptId で最大 3 回まで再試行する。 */
export const PDF_OUTSIDE_NOTICE_RETRY_DELAYS_MS: readonly number[] = [500, 1_500, 3_000];

/**
 * 確定待ちとして控える op の上限。応答が届かないまま積み上がり続けないようにする。1 回の操作（一括の
 * 貼り付けなど）で作れる図形の数はキャンバスのオブジェクト数の上限を超えないため、その 2 倍にして、
 * 上限いっぱいの一括作成に未確定の移動などが重なっても候補を確定前に捨てない。これを超えるのは確定が
 * 長く届かない異常時だけで、その場合は古い候補から捨てる（警告を見送るだけで、作品には影響しない）。
 */
const PENDING_PLACEMENT_LIMIT = CANVAS_OBJECT_HARD_LIMIT * 2;

/** 水位より先に届いた op.commit（他者の操作を含む）を控える上限。古いものから捨てる。 */
const BUFFERED_COMMIT_LIMIT = 2_000;

/** 移動・リサイズ・回転とみなす transform の項目（ロック・重なり順だけの変更は配置ではない）。 */
const GEOMETRY_KEYS = ["x", "y", "w", "h", "rotation"] as const;

const generateAttemptId = customAlphabet(
  "23456789abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ",
  21
);

/**
 * タブ内（ページを開いている間）で共有する記録。キャンバスを切り替えて戻っても引き継ぐ。
 * キーは利用者 × キャンバス（noticeScopeKey）。
 */
export interface PdfBoundaryNoticeTabMemory {
  /** 表示権の勝敗が決まった（サーバから応答を受け取った）利用者 × キャンバス。このタブでは再要求しない。 */
  readonly settledScopes: Set<string>;
  /** 応答を受け取れなかった要求の attemptId（利用者 × キャンバスごと）。次の要求でも同じ値を使う。 */
  readonly unsettledAttemptIds: Map<string, string>;
  /** このタブで表示済みの attemptId。同じ表示権の応答を二重に表示しない。 */
  readonly consumedAttemptIds: Set<string>;
}

export function createPdfBoundaryNoticeTabMemory(): PdfBoundaryNoticeTabMemory {
  return {
    settledScopes: new Set(),
    unsettledAttemptIds: new Map(),
    consumedAttemptIds: new Set(),
  };
}

const TAB_MEMORY = createPdfBoundaryNoticeTabMemory();

/** タブ内の記録のキー。利用者 ID とキャンバス ID の組を、区切り文字に依らず一意な文字列にする。 */
function noticeScopeKey(userId: string, canvasId: string): string {
  return JSON.stringify([userId, canvasId]);
}

/** 判定に使うサーバの確定状態（doc store の committed 側。未確定の楽観適用を含まない）。 */
export interface ConfirmedCanvasState {
  /** serverSeq の欠番なく確定状態へ畳み込んだ最大の serverSeq（doc store の serverSeq）。 */
  readonly serverSeq: number;
  /** 確定状態の図形（committedObjects）。コネクタの端点の参照にも使う。 */
  readonly objects: CanvasObjectLookup;
  /** 確定状態のサイズプリセット（committedMeta）から求めた PDF 出力範囲（キャンバス矩形）。 */
  readonly canvasRect: CanvasFrame;
}

export interface PdfBoundaryNoticeControllerDeps {
  readonly canvasId: string;
  /** 表示権の持ち主（認証済みの利用者）。タブ内の記録をキャンバスと組にして分ける。 */
  readonly userId: string;
  readonly memory: PdfBoundaryNoticeTabMemory;
  /** サーバへ表示権を要求する。 */
  readonly claim: (attemptId: string) => Promise<{ readonly show: boolean }>;
  /** 警告をキャンバス内のお知らせへ積む。 */
  readonly notify: (message: string) => void;
  /** 現在のサーバの確定状態。 */
  readonly getConfirmedState: () => ConfirmedCanvasState;
  /**
   * 確定状態の畳み込みが進んだ（serverSeq が変わった）ときに listener を呼ぶよう購読する。解除関数を返す。
   * 欠番で保留されていた確定が、先行する確定の到着で畳み込まれたときの判定に使う。
   */
  readonly subscribeConfirmedState: (listener: () => void) => () => void;
  /** 競合で再送され clientOpId が差し替わった op を探す。見つからなければ undefined。 */
  readonly findRetriedOp: (rejected: CanvasOp) => CanvasOp | undefined;
  readonly generateAttemptId: () => string;
  readonly wait: (ms: number) => Promise<void>;
  /** 同じメッセージを受け取る他の購読者（再送の処理）が終わった後に実行する。 */
  readonly defer: (task: () => void) => void;
}

export interface PdfBoundaryNoticeController {
  readonly trackPlacementOps: (ops: readonly CanvasOp[]) => void;
  readonly handleServerMessage: (message: ServerMessage) => void;
  readonly dispose: () => void;
}

/**
 * 1 つのキャンバスを開いている間の判定と表示権の要求を受け持つ。React に依存しない形にして、
 * 確定・拒否・再送・通信失敗の組み合わせを単体テストで再現できるようにする。
 */
export function createPdfBoundaryNoticeController(
  deps: PdfBoundaryNoticeControllerDeps
): PdfBoundaryNoticeController {
  const pending = new Map<string, CanvasOp>();
  /** 確定（op.commit）を受け取り、確定状態へ畳み込まれるのを待っている配置操作（serverSeq の昇順）。 */
  const awaitingFold: { readonly op: CanvasOp; readonly serverSeq: number }[] = [];
  /**
   * 受け取った時点で doc store の水位より先にあった op.commit（欠番で保留され、後でまとめて畳み込まれる。
   * 他者の操作を含む。serverSeq → op）。欠番が埋まってまとめて畳み込まれたとき、判定する配置より後の確定が
   * 判定の材料を変えたかを確かめる。op.commit でまとめて畳み込まれた場合、判定する配置より後の確定は
   * 必ずこの控えにある（まとまりを畳み込ませた確定は、まとまりの中でいちばん小さい serverSeq のため）。
   * スナップショットの取り直し（resync）で水位が進んだ場合は、取り直した確定状態で判定する。
   */
  const bufferedCommits = new Map<number, CanvasOp>();
  const scope = noticeScopeKey(deps.userId, deps.canvasId);
  let claimState: "idle" | "claiming" = "idle";
  let disposed = false;

  const isSettled = (): boolean => deps.memory.settledScopes.has(scope);

  const track = (op: CanvasOp): void => {
    if (placementTargetOf(op) === null) return;
    pending.set(op.clientOpId, op);
    if (pending.size > PENDING_PLACEMENT_LIMIT) {
      const oldest = pending.keys().next().value;
      if (oldest !== undefined) pending.delete(oldest);
    }
  };

  const requestClaim = async (): Promise<void> => {
    if (disposed || claimState !== "idle" || isSettled()) return;
    claimState = "claiming";
    const attemptId = deps.memory.unsettledAttemptIds.get(scope) ?? deps.generateAttemptId();
    deps.memory.unsettledAttemptIds.set(scope, attemptId);
    for (let attempt = 0; attempt <= PDF_OUTSIDE_NOTICE_RETRY_DELAYS_MS.length; attempt += 1) {
      if (attempt > 0) {
        await deps.wait(PDF_OUTSIDE_NOTICE_RETRY_DELAYS_MS[attempt - 1] ?? 0);
        if (disposed) return;
      }
      let result: { readonly show: boolean };
      try {
        result = await deps.claim(attemptId);
      } catch (error) {
        if (disposed) return;
        if (!isRetriableClaimError(error)) break;
        continue;
      }
      claimState = "idle";
      if (disposed) {
        // キャンバスを切り替えた後に届いた応答は、新しいキャンバスへ表示しない。勝者の応答なら attemptId を
        // 未確定のまま残し、このキャンバスへ戻って次に範囲外への配置が確定したとき、同じ attemptId で
        // 受け取り直す。
        if (!result.show) {
          deps.memory.settledScopes.add(scope);
          deps.memory.unsettledAttemptIds.delete(scope);
        }
        return;
      }
      // 応答を受け取った時点で勝敗は決まっている（表示しない側の応答も含む）。
      deps.memory.settledScopes.add(scope);
      deps.memory.unsettledAttemptIds.delete(scope);
      pending.clear();
      awaitingFold.length = 0;
      bufferedCommits.clear();
      if (result.show && !deps.memory.consumedAttemptIds.has(attemptId)) {
        deps.memory.consumedAttemptIds.add(attemptId);
        deps.notify(PDF_OUTSIDE_NOTICE_MESSAGE);
      }
      return;
    }
    // 未通知のまま残し、次に範囲外への配置が確定したときに同じ attemptId から要求し直す。
    claimState = "idle";
  };

  /**
   * serverSeq が afterSeq より後で upToSeq 以下の確定（同じまとまりで畳み込まれた後続の確定）をすべて控えて
   * いて、そのどれも判定の材料（objectIds の図形・出力範囲）を変えていないか。控えが欠けている（スナップ
   * ショットの取り直しで水位が進んだ、上限を超えて古い控えを捨てた）ときは、後続の影響を確かめられないため
   * false を返す（判定しない側へ倒す）。
   */
  const laterCommitsKeepJudgement = (
    afterSeq: number,
    upToSeq: number,
    objectIds: ReadonlySet<string>
  ): boolean => {
    if (upToSeq - afterSeq > BUFFERED_COMMIT_LIMIT) return false;
    for (let serverSeq = afterSeq + 1; serverSeq <= upToSeq; serverSeq += 1) {
      const op = bufferedCommits.get(serverSeq);
      if (op === undefined || commitChangesJudgement(op, objectIds)) return false;
    }
    return true;
  };

  /**
   * 確定状態へ畳み込まれた配置操作の結果を判定する。対象の図形は、その op を含む確定状態から引く
   * （作成・ペンはサーバが確定した図形、移動等はその確定値を反映した図形、画像・動画の公開 URL の反映は
   * 確定済みの枠）。確定状態に無い図形（後続の確定で削除された等）は判定しない。
   */
  const evaluateConfirmed = (
    entry: { readonly op: CanvasOp; readonly serverSeq: number },
    state: ConfirmedCanvasState
  ): void => {
    if (claimState !== "idle" || isSettled()) return;
    const targetId = placementTargetOf(entry.op);
    if (targetId === null) return;
    const confirmed = state.objects[targetId];
    if (!confirmed) return;
    // 画像・動画の仮表示（アップロードの完了前）は、アップロードの失敗で取り消され得るため判定しない。
    // 仮表示の間に動かしていても、アップロードが成功して公開 URL を反映した確定の時点で判定する。
    if (isPendingAttachmentObject(confirmed)) return;
    // 後続の確定とまとめて畳み込まれたとき、その後続が判定の材料を変えた（または確かめられない）なら、
    // 確定状態はこの配置の結果を表さないため判定しない。
    if (
      entry.serverSeq < state.serverSeq &&
      !laterCommitsKeepJudgement(entry.serverSeq, state.serverSeq, judgementObjectIdsOf(confirmed))
    ) {
      return;
    }
    if (!isObjectOutsidePdfFrame(confirmed, state.objects, state.canvasRect)) return;
    void requestClaim();
  };

  /** 確定状態の水位（serverSeq）以下まで畳み込まれた配置操作を、serverSeq の昇順に判定する。 */
  const evaluateFolded = (): void => {
    if (disposed || awaitingFold.length === 0) return;
    const state = deps.getConfirmedState();
    while (awaitingFold.length > 0) {
      const next = awaitingFold[0];
      if (!next || next.serverSeq > state.serverSeq) break;
      awaitingFold.shift();
      evaluateConfirmed(next, state);
    }
  };

  const rememberBufferedCommit = (op: CanvasOp, serverSeq: number): void => {
    // 水位の次の確定は保留されずにすぐ畳み込まれるため、まとまりの「後続」にはならない。
    if (serverSeq <= deps.getConfirmedState().serverSeq + 1) return;
    bufferedCommits.set(serverSeq, op);
    if (bufferedCommits.size > BUFFERED_COMMIT_LIMIT) {
      const oldest = bufferedCommits.keys().next().value;
      if (oldest !== undefined) bufferedCommits.delete(oldest);
    }
  };

  const awaitFold = (op: CanvasOp, serverSeq: number): void => {
    // 確定は順不同で届き得るため、serverSeq の昇順を保って控える。
    const index = awaitingFold.findIndex((entry) => entry.serverSeq > serverSeq);
    if (index < 0) awaitingFold.push({ op, serverSeq });
    else awaitingFold.splice(index, 0, { op, serverSeq });
    if (awaitingFold.length > PENDING_PLACEMENT_LIMIT) awaitingFold.shift();
  };

  const unsubscribeConfirmedState = deps.subscribeConfirmedState(evaluateFolded);

  return {
    trackPlacementOps(ops) {
      if (disposed || isSettled()) return;
      for (const op of ops) track(op);
    },
    handleServerMessage(message) {
      if (disposed) return;
      if (message.type === "op.commit") {
        rememberBufferedCommit(message.op, message.serverSeq);
        const tracked = pending.get(message.op.clientOpId);
        if (!tracked) return;
        pending.delete(message.op.clientOpId);
        if (isSettled()) return;
        // doc store が同じ確定を先に畳み込んでいれば、ここで判定する。まだなら（購読の順序や欠番で）
        // 畳み込まれた時点の購読の通知で判定する。
        awaitFold(message.op, message.serverSeq);
        evaluateFolded();
        return;
      }
      if (message.type === "op.reject") {
        const tracked = pending.get(message.clientOpId);
        if (!tracked) return;
        pending.delete(message.clientOpId);
        // 競合した移動は、同じメッセージの購読者（realtime 接続）が新しい clientOpId で 1 回だけ再送する。
        // 購読の順序に依らないよう、その処理が終わってから再送後の op を探して控え直す。
        deps.defer(() => {
          if (disposed) return;
          const retried = deps.findRetriedOp(tracked);
          if (retried && !pending.has(retried.clientOpId)) track(retried);
        });
      }
    },
    dispose() {
      disposed = true;
      pending.clear();
      awaitingFold.length = 0;
      bufferedCommits.clear();
      unsubscribeConfirmedState();
    },
  };
}

/**
 * 配置操作として判定の対象にする op なら、対象オブジェクトの id を返す。
 * PDF に描かれないコメントスレッドの作成、ロック・重なり順だけの変更、本人用の blob プレビューは対象外。
 */
export function placementTargetOf(op: CanvasOp): string | null {
  switch (op.type) {
    case "object.create":
      return isDrawableObject(op.object.type) ? op.object.id : null;
    case "pen.stroke.commit":
      return op.object.id;
    case "object.transform":
      return hasGeometryChange(op.transform) ? op.objectId : null;
    case "object.attachment.update":
      return op.src.startsWith("blob:") ? null : op.objectId;
    default:
      return null;
  }
}

function hasGeometryChange(transform: TransformObjectOp["transform"]): boolean {
  return GEOMETRY_KEYS.some((key) => transform[key] !== undefined);
}

/** アップロードの完了前の仮表示（src が仮の data: URL）の画像・動画か。 */
function isPendingAttachmentObject(object: CanvasObject): boolean {
  return (object.type === "image" || object.type === "video") && isPendingAttachmentSrc(object.src);
}

/** 判定の材料になる図形の id（対象の図形と、コネクタなら経路を決める端点の図形）。 */
function judgementObjectIdsOf(object: CanvasObject): ReadonlySet<string> {
  if (object.type === "connector") {
    return new Set([object.id, object.from.objectId, object.to.objectId]);
  }
  return new Set([object.id]);
}

/**
 * 確定した op が、判定の材料（objectIds の図形の外接矩形・PDF 出力範囲）を変え得るか。外接矩形を変えるのは
 * 位置・大きさ・回転の変更、削除、線の太さ（図形・コネクタの strokeWidth、ペンの thickness）の変更と、
 * 図形が描かれるかどうかを決める塗り・線の色（fill / stroke。透明への変更・透明からの変更）の変更で、
 * ロック・重なり順だけの変更、文字色などのほかの書式や本文の変更は数えない。メタの変更はサイズプリセットの
 * 変更だけを数える。
 */
function commitChangesJudgement(op: CanvasOp, objectIds: ReadonlySet<string>): boolean {
  switch (op.type) {
    case "canvas.meta.update":
      return op.sizePreset !== undefined;
    case "object.create":
    case "pen.stroke.commit":
      return objectIds.has(op.object.id);
    case "object.transform":
      return objectIds.has(op.objectId) && hasGeometryChange(op.transform);
    case "object.delete":
      return objectIds.has(op.objectId);
    case "object.style.update":
      return (
        objectIds.has(op.objectId) &&
        (op.style.strokeWidth !== undefined ||
          op.style.thickness !== undefined ||
          op.style.fill !== undefined ||
          op.style.stroke !== undefined)
      );
    default:
      return false;
  }
}

/**
 * 再試行で回復し得る失敗か。サーバの一時的な失敗（5xx・408・429）と通信そのものの失敗は再試行し、
 * 要求の内容や状態で決まる 4xx（キャンバスが無い・ゴミ箱・権限が無い等）は、その場では繰り返さない。
 */
function isRetriableClaimError(error: unknown): boolean {
  if (error instanceof CanvasApiHttpError) {
    return error.status >= 500 || error.status === 408 || error.status === 429;
  }
  return true;
}

/** realtime の再送（競合した transform の 1 回だけの再送）で差し替わった op を doc store から探す。 */
function findRetriedTransformOp(rejected: CanvasOp): CanvasOp | undefined {
  if (rejected.type !== "object.transform") return undefined;
  const state = useCanvasDocStore.getState();
  return state.inFlightOps.find(
    (op) =>
      op.type === "object.transform" &&
      op.objectId === rejected.objectId &&
      op.clientOpId !== rejected.clientOpId &&
      state.retriedClientOpIds[op.clientOpId] === true &&
      isSameTransform(op.transform, rejected.transform)
  );
}

function isSameTransform(
  a: TransformObjectOp["transform"],
  b: TransformObjectOp["transform"]
): boolean {
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]) as Set<keyof typeof a>;
  for (const key of keys) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}

export interface PdfBoundaryNoticeTracker {
  /** dispatcher の onLocalPlacementOps へ渡す。関数の同一性は canvas を切り替えても変わらない。 */
  readonly trackPlacementOps: (ops: readonly CanvasOp[]) => void;
}

/**
 * 編集ビューの CanvasRealtimeWorkspace で使う。返した trackPlacementOps を useCanvasOpDispatcher の
 * onLocalPlacementOps へ渡す。キャンバスまたは利用者が替わると判定をやり直し、前の判定の遅れた応答は表示しない。
 */
export function usePdfBoundaryNotice(params: {
  readonly canvasId: string;
  readonly userId: string;
}): PdfBoundaryNoticeTracker {
  const { canvasId, userId } = params;
  const realtime = useRealtimeClient();
  const canvasApi = useCanvasApi();
  const showToast = useShowToast();
  const canvasApiRef = useRef(canvasApi);
  canvasApiRef.current = canvasApi;
  const showToastRef = useRef(showToast);
  showToastRef.current = showToast;
  const controllerRef = useRef<PdfBoundaryNoticeController | null>(null);

  useEffect(() => {
    const controller = createPdfBoundaryNoticeController({
      canvasId,
      userId,
      memory: TAB_MEMORY,
      claim: (attemptId) => canvasApiRef.current.claimPdfOutsideNotice(canvasId, attemptId),
      notify: (message) => showToastRef.current("warning", message),
      getConfirmedState: () => {
        const state = useCanvasDocStore.getState();
        return {
          serverSeq: state.serverSeq,
          objects: state.committedObjects,
          canvasRect: resolveCanvasRect(normalizeCanvasSizePreset(state.committedMeta.sizePreset)),
        };
      },
      subscribeConfirmedState: (listener) =>
        useCanvasDocStore.subscribe((state, previous) => {
          if (state.serverSeq !== previous.serverSeq) listener();
        }),
      findRetriedOp: findRetriedTransformOp,
      generateAttemptId,
      wait: (ms) => new Promise((resolve) => setTimeout(resolve, ms)),
      defer: (task) => queueMicrotask(task),
    });
    controllerRef.current = controller;
    const unsubscribe = realtime.onMessage(controller.handleServerMessage);
    return () => {
      unsubscribe();
      controller.dispose();
      if (controllerRef.current === controller) controllerRef.current = null;
    };
  }, [canvasId, realtime, userId]);

  const trackPlacementOps = useCallback((ops: readonly CanvasOp[]): void => {
    controllerRef.current?.trackPlacementOps(ops);
  }, []);
  return { trackPlacementOps };
}
